import React from "react";
import { toast } from "react-toastify";
import { MessageCircle } from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import Navbar from "../../../Components/Common/navbar";
import { useAuthContext } from "../../../Components/AuthContext";
import MobileViewContainer from "../../../Components/MobileViewContainer";
import { couponService } from "../../../api/services/coupon.service";
import { siteService } from "../../../api/services/site.service";
import { shareKakao } from "../../../common/social/kakao/api";
import PromotionGiftLayout from "../../Promotion/components/PromotionGiftLayout";

function getErrorMessage(error) {
  if (!error) {
    return "";
  }

  if (typeof error === "string") {
    return error;
  }

  return error?.response?.data?.error || error?.response?.data?.detail || error?.message || "정보를 불러오지 못했습니다.";
}

function mapEventCouponItem(couponItem, siteNameMap, isLoggedIn) {
  const siteLines = (couponItem?.target_sites || [])
    .map((siteId) => siteNameMap[siteId])
    .filter(Boolean);

  const startDate = couponItem?.start_date;
  const endDate = couponItem?.end_date;

  return {
    type: "coupon",
    itemName: couponItem?.coupon_product?.title || "",
    imageUrl: couponItem?.coupon_product?.image || "",
    senderName: siteLines[0] || "",
    remainingCount: isLoggedIn ? Number(couponItem?.remain || 0) : 0,
    siteLines,
    validityText: startDate && endDate ? `${startDate} ~ ${endDate} 사용 가능` : "유효 기간 내 사용 가능",
    noticeHtml: couponItem?.coupon_product?.descript || "",
  };
}

const EventCouponPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { accessToken } = useAuthContext();

  const isLoggedIn = Boolean(accessToken);

  const [couponItem, setCouponItem] = React.useState(null);
  const [siteNameMap, setSiteNameMap] = React.useState({});
  const [isLoading, setIsLoading] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState(null);

  const fetchSiteMap = React.useCallback(async () => {
    try {
      const response = await siteService.getSitesApp();
      const sites = response?.data?.result?.sites || [];
      const nextMap = {};

      sites.forEach((site) => {
        if (site?.id) {
          nextMap[site.id] = site.name || "";
        }
      });

      setSiteNameMap(nextMap);
    } catch (e) {
      console.log(e);
    }
  }, []);

  const fetchCouponRule = React.useCallback(async () => {
    if (!id) {
      setCouponItem(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await couponService.getCouponBookPublic();
      const rules = Array.isArray(response?.data) ? response.data : [];
      const targetId = Number(id);
      const nextItem = rules.find((rule) => Number(rule.id) === targetId) || null;

      if (!nextItem) {
        setError("정보를 찾을 수 없습니다.");
      }
      setCouponItem(nextItem);
    } catch (e) {
      console.log(e);
      setError(e);
      setCouponItem(null);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  React.useEffect(() => {
    fetchSiteMap();
  }, [fetchSiteMap]);

  React.useEffect(() => {
    fetchCouponRule();
  }, [fetchCouponRule]);

  const isReceived = isLoggedIn && Boolean(couponItem?.downloaded);
  const isEventOpen = couponItem?.state === "노출중";

  const mappedItem = React.useMemo(() => {
    return mapEventCouponItem(couponItem, siteNameMap, isLoggedIn);
  }, [couponItem, siteNameMap, isLoggedIn]);

  const handlePrimaryAction = React.useCallback(async () => {
    if (!id) {
      return;
    }

    if (!isLoggedIn) {
      const redirectPath = `${location.pathname}${location.search || ""}`;
      navigate(`/login?path=${redirectPath}`);
      return;
    }

    if (!couponItem || isSubmitting) {
      return;
    }

    if (!isEventOpen) {
      toast.error("이벤트가 종료되었습니다.");
      return;
    }

    if (!couponItem.downloadable) {
      toast.error("다운로드할 수 없는 쿠폰입니다.");
      return;
    }

    if (isReceived) {
      toast.error("이미 쿠폰을 받으셨습니다.");
      return;
    }

    setIsSubmitting(true);
    try {
      await couponService.postCouponDownById(Number(id));
      toast.error("쿠폰을 받았습니다.");
      await fetchCouponRule();
    } catch (e) {
      console.log(e);
      setError(e);
      toast.error(getErrorMessage(e) || "쿠폰을 받을 수 없습니다.");
    } finally {
      setIsSubmitting(false);
    }
  }, [couponItem, fetchCouponRule, id, isEventOpen, isLoggedIn, isReceived, isSubmitting, location.pathname, location.search, navigate]);

  const handleShare = React.useCallback(() => {
    if (!couponItem) {
      return;
    }

    const imageUrl = couponItem?.coupon_product?.image || `https://${window.location.hostname}/amazing-logo.png`;
    const sharedSite = mappedItem?.siteLines?.[0];

    shareKakao({
      title: sharedSite || "AmazingTicket",
      description: couponItem?.coupon_product?.title || "할인쿠폰",
      imageUrl,
      shareUrl: window.location.href,
    });
  }, [couponItem, mappedItem?.siteLines]);

  const bottomAction = couponItem ? (
    <div className="rounded-[24px] border border-[#e4e3e0] bg-white px-6 py-6">
      {!isEventOpen ? (
        <p className="mb-4 text-center text-[17px] text-[#ff5d12]" style={{ fontFamily: "Gmarket-M" }}>
          이벤트가 종료되었습니다.
        </p>
      ) : null}

      <button
        type="button"
        onClick={handleShare}
        className="flex w-full items-center justify-center gap-2 rounded-[20px] px-6 py-4 text-[18px] text-white transition-opacity disabled:cursor-not-allowed disabled:bg-[#b8c0cd]"
        style={{
          fontFamily: "Gmarket-B",
          background: "linear-gradient(135deg, #ff7800 0%, #ff2d57 100%)",
        }}
        disabled={isLoading}
      >
        <MessageCircle size={22} />
        <span>카카오톡 공유하기</span>
      </button>
    </div>
  ) : null;

  return (
    <MobileViewContainer>
      <Navbar />
      <PromotionGiftLayout
        {...mappedItem}
        isLoggedIn={isLoggedIn}
        isLoading={isLoading}
        isSubmitting={isSubmitting}
        isReceived={isReceived}
        onPrimaryAction={handlePrimaryAction}
        error={error}
        bottomAction={bottomAction}
      />
    </MobileViewContainer>
  );
};

export default EventCouponPage;
